import { DesignPage, DesignTemplate } from "api/config/chalice-types";
import { StoreType } from "polotno/model/store";

/**
 * Generates thumbnail data URLs for each page in a Polotno store.
 * @param store - The Polotno store instance.
 * @param pages - The array of DesignPage objects.
 * @returns A Promise resolving to a Record mapping page IDs to thumbnail data URLs (or undefined if generation failed for a page).
 */
export async function generatePageThumbnailsMap(
  store: StoreType,
  pages: DesignPage[]
): Promise<Record<string, string | undefined>> {
  const thumbnailMap: Record<string, string | undefined> = {};

  try {
    // Ensure store is ready (might be redundant if called after interaction, but safe)
    await store.waitLoading();
    // Use a small delay just in case
    await new Promise((resolve) => setTimeout(resolve, 50));

    for (const page of pages) {
      if (!page.id) continue;
      try {
        const thumbnailUrl = await store.toDataURL({
          pageId: page.id,
          pixelRatio: 0.5, // Low resolution for previews
          mimeType: "image/png",
          quickMode: true,
        });
        thumbnailMap[page.id] = thumbnailUrl;
      } catch (pageError) {
        console.error(
          `[generatePageThumbnailsMap] Error generating thumbnail for page ${page.id}:`,
          pageError
        );
        thumbnailMap[page.id] = undefined; // Explicitly undefined on error
      }
    }
  } catch (globalError) {
    console.error(
      "[generatePageThumbnailsMap] Global error during thumbnail generation:",
      globalError
    );
    // If the whole process fails, return an empty map or map with undefined for all
    // Let's return undefined for all pages in case of a global error during the loop setup
    pages.forEach((p) => {
      if (p.id) thumbnailMap[p.id] = undefined;
    });
    // Re-throw or handle as needed - for now just log and return the map with undefineds
  }
  return thumbnailMap;
}

/**
 * Converts the current state of a Polotno store back into a DesignTemplate,
 * generating page thumbnails in the process.
 * @param store - The Polotno store instance.
 * @param baseTemplate - The original template to merge the store JSON into.
 * @returns A Promise resolving to the updated DesignTemplate.
 */
export async function getDesignTemplateFromStore(
  store: StoreType,
  baseTemplate: DesignTemplate
): Promise<DesignTemplate> {
  const polotnoJson = store.toJSON();
  let pages = polotnoJson.pages as DesignPage[];
  let thumbnailMap: Record<string, string | undefined> = {};

  try {
    thumbnailMap = await generatePageThumbnailsMap(store, pages);
  } catch (error) {
    console.error(
      "[getDesignTemplateFromStore] Failed to generate thumbnails map",
      error
    );
    // Proceed without thumbnails if map generation fails
  }

  pages = pages.map((page) => {
    if (!page.id) return page;
    const thumbnailUrl = thumbnailMap[page.id];

    const custom = {
      ...(page.custom ?? {}),
      colorType: page.custom?.colorType ?? "STATIC",
      image_url: thumbnailUrl,
    };

    return { ...page, custom };
  });

  const updatedTemplate: DesignTemplate = {
    ...baseTemplate, // Use the passed base template
    ...polotnoJson,
    pages: pages,
  };
  return updatedTemplate;
}
